// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { get } from '@tls/auth';
import { getEmaiVersionStorageKey } from './emaiVersion';

const UI_VERSION_NAME = 'sw91_ui';
const UI_VERSION_ENDPOINT = 'Version/ui';
const UI_VERSION_REFRESH_PARAM = 'versionRefresh';
const MAINTENANCE_START_REFRESH_TOKEN = 'maintenance-start';
const UI_RELOAD_STORAGE_PREFIX = 'sw91:uiReload:';
let reloadPending = false;

export function isUiReloadPending() {
  return reloadPending;
}

export function getUiVersionReloadStorageKey(appBaseUrl = '') {
  return `${UI_RELOAD_STORAGE_PREFIX}${appBaseUrl}`;
}

type VersionEntry = {
  name: string;
  version: string;
};

export type UiVersionStatus =
  | 'available'
  | 'missing'
  | 'temporarily-unavailable'
  | 'unreachable'
  | 'error'
  | 'deferred'
  | 'reloading';

type UiVersionCheckOptions = {
  forceReload?: boolean;
  appBaseUrl?: string;
  signal?: AbortSignal;
};

type HostedUiVersionResult =
  | { status: 'available'; version: string }
  | { status: Exclude<UiVersionStatus, 'available' | 'error' | 'reloading' | 'deferred'> }
  | { status: 'error'; httpStatus?: number };

export async function checkHostedUiVersion(options: UiVersionCheckOptions = {}): Promise<UiVersionStatus> {
  if (reloadPending) return 'reloading';
  if (options.signal?.aborted) return 'deferred';
  const hostedUiVersion = await getHostedUiVersion();
  if (reloadPending) return 'reloading';
  if (options.signal?.aborted) return 'deferred';

  if (hostedUiVersion.status === 'missing') {
    console.warn(`Hosted UI version entry '${UI_VERSION_NAME}' was not found.`);
    return hostedUiVersion.status;
  }

  if (hostedUiVersion.status === 'error') {
    console.warn('Hosted UI version request failed.', hostedUiVersion.httpStatus);
    return hostedUiVersion.status;
  }

  if (hostedUiVersion.status !== 'available') {
    return hostedUiVersion.status;
  }

  const backendUiVersion = hostedUiVersion.version.trim();
  clearUiVersionRefreshParameter();

  if (__APP_VERSION__.trim() === backendUiVersion && !options.forceReload) {
    clearReloadMarker(options.appBaseUrl);
    return hostedUiVersion.status;
  }

  const result = await clearBrowserStateAndReload(`version:${backendUiVersion}`, options);
  return result === 'skipped' ? 'available' : result;
}

export async function refreshUiForMaintenanceStart(options: UiVersionCheckOptions = {}): Promise<UiVersionStatus> {
  const result = await clearBrowserStateAndReload(MAINTENANCE_START_REFRESH_TOKEN, options);
  return result === 'skipped' ? 'temporarily-unavailable' : result;
}

export async function getHostedUiVersion(): Promise<HostedUiVersionResult> {
  try {
    const response = await get(UI_VERSION_ENDPOINT, false, true, {
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
      params: {
        _: Date.now(),
      },
    });

    const status = Number(response?.status);

    if (status === 503) {
      return { status: 'temporarily-unavailable' };
    }

    if (!status) {
      return { status: 'unreachable' };
    }

    if (status === 404) {
      return { status: 'missing' };
    }

    if (status >= 400) {
      return { status: 'error', httpStatus: status };
    }

    const uiVersion = response?.data as VersionEntry | undefined;

    if (uiVersion?.name !== UI_VERSION_NAME || typeof uiVersion.version !== 'string' || !uiVersion.version.trim()) {
      return { status: 'missing' };
    }

    return { status: 'available', version: uiVersion.version };
  } catch (error) {
    console.warn('Hosted UI version request failed.', error);
    return { status: 'unreachable' };
  }
}

function clearReloadMarker(appBaseUrl?: string) {
  try {
    sessionStorage.removeItem(getUiVersionReloadStorageKey(appBaseUrl));
  } catch (error) {
    console.warn('Unable to clear the UI reload marker.', error);
  }
}

async function clearBrowserStateAndReload(
  refreshToken: string,
  options: UiVersionCheckOptions
): Promise<'reloading' | 'skipped' | 'deferred' | 'error'> {
  if (reloadPending) return 'reloading';
  if (options.signal?.aborted) return 'deferred';

  try {
    const key = getUiVersionReloadStorageKey(options.appBaseUrl);
    if (sessionStorage.getItem(key) === refreshToken) {
      console.warn(`UI reload already attempted for '${refreshToken}'.`);
      return 'skipped';
    }
    sessionStorage.setItem(key, refreshToken);
    if (sessionStorage.getItem(key) !== refreshToken) throw new Error('UI reload marker was not saved');
  } catch (error) {
    console.warn('Unable to persist the UI reload marker; automatic reload skipped.', error);
    return 'error';
  }

  // Once storage cleanup starts, the reload must finish even if the caller aborts.
  reloadPending = true;
  try {
    clearStorageExcept(localStorage, getEmaiVersionStorageKey());
    clearStorageExcept(sessionStorage, UI_RELOAD_STORAGE_PREFIX);
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map(name => caches.delete(name)));
    }
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(registration => registration.unregister()));
    }
  } catch (error) {
    console.warn('Failed to clear browser state before UI reload.', error);
  }

  const url = new URL(window.location.href);
  url.searchParams.delete(UI_VERSION_REFRESH_PARAM);
  window.location.replace(url.toString());
  return 'reloading';
}

function clearStorageExcept(storage: Storage, preservedPrefix: string) {
  for (let index = storage.length - 1; index >= 0; index--) {
    const key = storage.key(index);
    if (key !== null && !key.startsWith(preservedPrefix)) storage.removeItem(key);
  }
}

function clearUiVersionRefreshParameter() {
  const url = new URL(window.location.href);

  if (!url.searchParams.has(UI_VERSION_REFRESH_PARAM)) {
    return;
  }

  url.searchParams.delete(UI_VERSION_REFRESH_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());
}
